import React, { useState, useEffect, useCallback } from 'react';
import ProfileHeader from './ProfileHeader';
import FavoritesGrid from './FavoritesGrid';
import VotesList from './VotesList';
import ThreadsList from './ThreadsList';
import NotificationsPanel from './NotificationsPanel';
import ActivityPanel from './ActivityPanel';
import { getAuthToken, getAuthHeaders } from '../../utils/auth';
import { Loader2, AlertCircle, RefreshCw, LogIn } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SORA = "'Sora', system-ui, sans-serif";
const MANROPE = "'Manrope', system-ui, sans-serif";
const CARD_BG = 'linear-gradient(180deg,#151420,#101018)';
const CARD_BORDER = '#232130';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'favorites', label: 'Favorites' },
  { key: 'activity', label: 'Activity' },
  { key: 'notifications', label: 'Notifications' },
];

const MySpaceContent = ({ onThreadClick, onLogin, onChannelClick, onContentClick }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [votes, setVotes] = useState([]);
  const [threads, setThreads] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activities, setActivities] = useState([]);
  const [contentLikes, setContentLikes] = useState([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [tab, setTab] = useState('overview');
  const unreadCount = notifications.filter(n => !n.read).length;
  const summary = {
    favorites: favorites.length + contentLikes.length,
    votes: votes.length,
    threads: threads.length,
    unread: unreadCount,
    activity: activities.length
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchMySpaceData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const token = getAuthToken();
      if (!token) {
        setError('Please log in to view your MySpace');
        setLoading(false);
        return;
      }

      const dashboardResponse = await fetch(`${API_URL}/myspace/dashboard`, {
        headers: getAuthHeaders(),
        cache: 'no-store'
      });

      if (!dashboardResponse.ok) {
        const errorData = await dashboardResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch dashboard data');
      }

      const dashboardData = await dashboardResponse.json();
      console.log('📊 [MySpace] dashboard response:', dashboardData);

      if (dashboardData.success) {
        const data = dashboardData.data;
        setUserData(data.user);
        setFavorites(data.favorites || []);
        setVotes(data.votes || []);
        const allThreads = [...(data.threads?.authored || []), ...(data.threads?.replied || [])];
        setThreads(allThreads);
        setActivities(data.activities || []);
        setContentLikes(data.contentLikes || []);
        console.log('📊 [MySpace] contentLikes:', data.contentLikes?.length, 'favorites:', data.favorites?.length);
      }

      const notificationsResponse = await fetch(`${API_URL}/myspace/notifications?limit=20`, {
        headers: getAuthHeaders()
      });

      if (notificationsResponse.ok) {
        const notificationsData = await notificationsResponse.json();
        if (notificationsData.success) {
          setNotifications(notificationsData.notifications || []);
        }
      }

    } catch (err) {
      console.error('❌ [MySpace] fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchMySpaceData(); }, [fetchMySpaceData]);

  if (loading) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0a0f',
      }}>
        <Loader2 size={32} color="#8B5CF6" style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0a0f',
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center',
          maxWidth: '400px',
        }}>
          <AlertCircle size={32} color="#EF4444" style={{ marginBottom: '12px' }} />
          <p style={{ color: '#FFFFFF', margin: 0, marginBottom: onLogin ? '16px' : 0, fontFamily: MANROPE }}>{error}</p>
          {onLogin && (
            <button
              onClick={onLogin}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: isMobile ? '100%' : 'auto',
                minHeight: '40px',
                padding: '10px 18px',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '700',
                fontFamily: MANROPE,
                cursor: 'pointer',
              }}
            >
              <LogIn size={16} />
              Log in
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      background: 'radial-gradient(circle at top left, rgba(139, 92, 246, 0.12) 0%, rgba(10,10,15,0) 30%), #0a0a0f',
      padding: isMobile ? '14px' : '24px',
      overflowY: 'auto',
      boxSizing: 'border-box',
      fontFamily: MANROPE,
    }}>
      <div style={{ maxWidth: '1440px', margin: '0 auto', minWidth: 0 }}>
        <ProfileHeader
          user={userData}
          onProfileUpdate={(updatedUser) => setUserData({ ...userData, ...updatedUser })}
          isMobile={isMobile}
          summary={summary}
        />

        <div style={{
          display: 'flex',
          gap: '6px',
          background: '#141018',
          border: `1px solid ${CARD_BORDER}`,
          borderRadius: '14px',
          padding: '6px',
          marginBottom: isMobile ? '14px' : '18px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  background: active ? 'linear-gradient(135deg,#8b5cf6,#ec4899)' : 'transparent',
                  color: active ? '#fff' : '#8f8b9c',
                  border: 'none', borderRadius: '10px', padding: '10px 18px',
                  fontSize: '13.5px', fontWeight: active ? '700' : '600',
                  fontFamily: MANROPE, cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
                }}
              >
                {t.label}
                {t.key === 'notifications' && unreadCount > 0 && (
                  <span style={{
                    marginLeft: '7px', background: active ? 'rgba(255,255,255,0.25)' : '#dc2626',
                    color: '#fff', fontSize: '10px', fontWeight: '700',
                    padding: '1px 6px', borderRadius: '999px',
                  }}>{unreadCount}</span>
                )}
              </button>
            );
          })}

          <button
            onClick={() => fetchMySpaceData(true)}
            disabled={refreshing}
            title="Refresh"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              padding: '10px 14px', borderRadius: '10px', border: 'none',
              background: 'transparent', color: '#8f8b9c', fontSize: '12px',
              fontWeight: '600', fontFamily: MANROPE,
              cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.6 : 1,
              marginLeft: 'auto',
            }}
            onMouseEnter={(e) => { if (!refreshing) e.currentTarget.style.color = '#c4b5fd'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#8f8b9c'; }}
          >
            <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>

        {tab === 'overview' && isMobile && (
          <div style={{
            background: unreadCount > 0 ? 'linear-gradient(180deg,#1d1712,#151008)' : CARD_BG,
            borderRadius: '16px',
            border: unreadCount > 0 ? '1px solid rgba(245, 158, 11, 0.28)' : `1px solid ${CARD_BORDER}`,
            padding: '16px',
            marginBottom: '14px',
            minWidth: 0,
            boxSizing: 'border-box',
          }}>
            <NotificationsPanel notifications={notifications} compact priority />
          </div>
        )}

        {tab === 'overview' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1.2fr) minmax(320px, 0.8fr)',
            gap: isMobile ? '14px' : '22px',
            minWidth: 0,
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '20px', minWidth: 0 }}>
              <FavoritesGrid favorites={favorites} contentLikes={contentLikes} onChannelClick={onChannelClick} onContentClick={onContentClick} />

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
                gap: isMobile ? '12px' : '20px',
                minWidth: 0,
              }}>
                <div style={{
                  background: CARD_BG,
                  borderRadius: isMobile ? '16px' : '18px',
                  border: `1px solid ${CARD_BORDER}`,
                  padding: isMobile ? '16px' : '20px',
                  maxHeight: isMobile ? 'none' : '400px',
                  overflowY: isMobile ? 'visible' : 'auto',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  boxShadow: '0 14px 28px rgba(0,0,0,0.2)'
                }}>
                  <ThreadsList threads={threads} onThreadClick={onThreadClick} compact />
                </div>

                <div style={{
                  background: CARD_BG,
                  borderRadius: isMobile ? '16px' : '18px',
                  border: `1px solid ${CARD_BORDER}`,
                  padding: isMobile ? '16px' : '20px',
                  maxHeight: isMobile ? 'none' : '400px',
                  overflowY: isMobile ? 'visible' : 'auto',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  boxShadow: '0 14px 28px rgba(0,0,0,0.2)'
                }}>
                  <VotesList votes={votes} compact />
                </div>
              </div>
            </div>

            <div style={{ display: isMobile ? 'none' : 'flex', flexDirection: 'column', gap: '20px', minWidth: 0, height: '100%', minHeight: 0 }}>
              <div style={{
                background: unreadCount > 0 ? 'linear-gradient(180deg,#1d1712,#151008)' : CARD_BG,
                borderRadius: '18px',
                border: unreadCount > 0 ? '1px solid rgba(245, 158, 11, 0.2)' : `1px solid ${CARD_BORDER}`,
                padding: '20px',
                maxHeight: '380px',
                overflowY: 'auto',
                minWidth: 0,
                boxSizing: 'border-box',
                boxShadow: '0 14px 28px rgba(0,0,0,0.2)'
              }}>
                <NotificationsPanel notifications={notifications} compact />
              </div>

              <div style={{
                background: CARD_BG,
                borderRadius: isMobile ? '16px' : '18px',
                border: `1px solid ${CARD_BORDER}`,
                padding: isMobile ? '16px' : '20px',
                flex: '0 0 420px',
                maxHeight: '420px',
                overflow: 'hidden',
                minWidth: 0,
                minHeight: 0,
                boxSizing: 'border-box',
                boxShadow: '0 14px 28px rgba(0,0,0,0.2)'
              }}>
                <ActivityPanel activities={activities} compact />
              </div>
            </div>
          </div>
        )}

        {tab === 'favorites' && (
          <FavoritesGrid favorites={favorites} contentLikes={contentLikes} onChannelClick={onChannelClick} onContentClick={onContentClick} />
        )}

        {tab === 'activity' && (
          <div style={{
            background: CARD_BG,
            borderRadius: isMobile ? '16px' : '20px',
            border: `1px solid ${CARD_BORDER}`,
            padding: isMobile ? '16px' : '22px',
            boxSizing: 'border-box',
            boxShadow: '0 16px 32px rgba(0,0,0,0.22)',
          }}>
            <ActivityPanel activities={activities} limit={50} />
          </div>
        )}

        {tab === 'notifications' && (
          <div style={{
            background: unreadCount > 0 ? 'linear-gradient(180deg,#1d1712,#151008)' : CARD_BG,
            borderRadius: isMobile ? '16px' : '20px',
            border: unreadCount > 0 ? '1px solid rgba(245, 158, 11, 0.2)' : `1px solid ${CARD_BORDER}`,
            padding: isMobile ? '16px' : '22px',
            boxSizing: 'border-box',
            boxShadow: '0 16px 32px rgba(0,0,0,0.22)',
          }}>
            <NotificationsPanel notifications={notifications} limit={50} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MySpaceContent;
